import React, { useEffect, useState } from 'react';
import classnames from 'clsx';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import semanticClassNames from '../../../Pagination.semanticClassNames';
import type { IResponsivePaginationProps } from '../../../Pagination.types';
import { NavigationButton } from '../../components/responsive/NavigationButton';
import classes from './style/ResponsivePagination.scss';
import { First, Last, Previous, Next } from '../../assets/responsive';
import { dataHooks } from './constants';
import { isNextEnabled, isPreviousEnabled } from '../../../utils';
import { getAccessibilityAttributes } from '../../../../../common/a11y';

const ResponsivePagination = (props: IResponsivePaginationProps) => {
  const {
    id,
    customClassNames = [],
    className,
    onChange,
    onNextClick,
    onPrevClick,
    currentPage,
    totalPages,
    firstText,
    lastText,
    nextText,
    previousText,
    isDisabled,
    onClick,
    onDblClick,
    onMouseEnter,
    onMouseLeave,
    forceNextButtonEnabled,
    forcePreviousButtonEnabled,
    direction,
    ariaLabelTranslations,
    a11y,
    ariaAttributes,
    partToPreviewStateMap,
    isOnStage,
    paginationLayout,
  } = props;

  const previousButtonRef = React.useRef<HTMLButtonElement>(null);
  const nextButtonRef = React.useRef<HTMLButtonElement>(null);
  const [shouldFocusNavigationButtons, setShouldFocusNavigationButtons] =
    useState(false);

  const buttonDataPreview = partToPreviewStateMap?.['navigation-button'];
  const shouldDisable = isDisabled || !totalPages;
  const isTextMode = paginationLayout === 'text';

  const a11yProps = getAccessibilityAttributes({
    ariaAttributes: { label: a11y?.label, ...ariaAttributes },
  });

  const isPreviousButtonEnabled = isPreviousEnabled({
    forcePreviousButtonEnabled,
    isDisabled: shouldDisable,
    currentPage,
  });
  const isNextButtonEnabled = isNextEnabled({
    forceNextButtonEnabled,
    isDisabled: shouldDisable,
    currentPage,
    totalPages,
  });

  useEffect(() => {
    if (shouldFocusNavigationButtons) {
      if (isNextButtonEnabled && !isPreviousButtonEnabled) {
        nextButtonRef?.current?.focus();
      }

      if (isPreviousButtonEnabled && !isNextButtonEnabled) {
        previousButtonRef?.current?.focus();
      }

      setShouldFocusNavigationButtons(false);
    }
  }, [
    isNextButtonEnabled,
    isPreviousButtonEnabled,
    shouldFocusNavigationButtons,
  ]);

  const onChangePage = (page: number) => {
    setShouldFocusNavigationButtons(true);
    onChange({
      type: 'change',
      target: { value: { currentPage: page } },
    });
  };

  const onClickNext = () => {
    onNextClick?.({ type: 'onNextClick' });
    onChangePage(currentPage + 1);
  };

  const onClickPrevious = () => {
    onPrevClick?.({ type: 'onPrevClick' });
    onChangePage(currentPage - 1);
  };

  return (
    <ul
      id={id}
      aria-label={
        a11yProps.ariaAttributes['aria-label'] ||
        ariaLabelTranslations.paginationLabel
      }
      data-hook={dataHooks.root}
      className={classnames(
        classes.root,
        className,
        formatClassNames(semanticClassNames.root, ...customClassNames),
      )}
      {...getDataAttributes(props)}
      dir={direction}
      {...(!shouldDisable && {
        onClick,
        onDoubleClick: onDblClick,
        onMouseEnter,
        onMouseLeave,
      })}
    >
      <li>
        <NavigationButton
          ariaLabel={isTextMode ? firstText : ariaLabelTranslations.firstPage}
          className={formatClassNames(semanticClassNames.navigationButton)}
          dataHook={dataHooks.navigationButtons.first}
          onClick={() => onChangePage(1)}
          label={firstText}
          icon={<First />}
          isDisabled={shouldDisable || currentPage <= 1}
          buttonType="first"
          dataPreview={buttonDataPreview}
        />
      </li>
      <li className={classes.previous}>
        <NavigationButton
          ref={previousButtonRef}
          ariaLabel={
            isTextMode ? previousText : ariaLabelTranslations.previousPage
          }
          className={formatClassNames(semanticClassNames.navigationButton)}
          dataHook={dataHooks.navigationButtons.previous}
          onClick={onClickPrevious}
          label={previousText}
          icon={<Previous />}
          isDisabled={!isPreviousButtonEnabled}
          buttonType="previous"
          dataPreview={buttonDataPreview}
        />
      </li>
      <li aria-live="polite" aria-atomic>
        <div className={classes.page} data-hook={dataHooks.currentPage}>
          <div className={classes.label} aria-hidden>
            {currentPage}
          </div>
          <span
            className={classes.visuallyHidden}
            data-hook={dataHooks.currentPageAriaLabel}
          >
            {ariaLabelTranslations.currentPage.replace(
              '{{currentPage}}',
              `${currentPage}`,
            )}
          </span>
        </div>
      </li>
      <li className={classes.next}>
        <NavigationButton
          ref={nextButtonRef}
          ariaLabel={isTextMode ? nextText : ariaLabelTranslations.nextPage}
          className={formatClassNames(semanticClassNames.navigationButton)}
          dataHook={dataHooks.navigationButtons.next}
          onClick={onClickNext}
          label={nextText}
          icon={<Next />}
          isDisabled={!isOnStage && !isNextButtonEnabled}
          buttonType="next"
          dataPreview={buttonDataPreview}
        />
      </li>
      <li>
        <NavigationButton
          ariaLabel={isTextMode ? lastText : ariaLabelTranslations.lastPage}
          className={formatClassNames(semanticClassNames.navigationButton)}
          dataHook={dataHooks.navigationButtons.last}
          onClick={() => onChangePage(totalPages)}
          label={lastText}
          icon={<Last />}
          isDisabled={
            !isOnStage && (shouldDisable || currentPage >= totalPages)
          }
          buttonType="last"
          dataPreview={buttonDataPreview}
        />
      </li>
    </ul>
  );
};

export default ResponsivePagination;
